import {Fragment, useContext, useRef} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import Currency from "@autocx/forms/src/controls/currency";
import {WebsiteContext} from "../../../index";

const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, {
    numeric: 'auto'
});

function formatPlacedAt(placedAtUtc) {
    if (!placedAtUtc) return 'Time unavailable';

    const placedAt = new Date(placedAtUtc);
    if (Number.isNaN(placedAt.getTime())) return 'Time unavailable';

    const secondsAgo = Math.floor((Date.now() - placedAt.getTime()) / 1000);

    if (secondsAgo >= 0 && secondsAgo < 60) return relativeTimeFormatter.format(0, 'second');
    if (secondsAgo < 3600) {
        const minutesAgo = Math.floor(secondsAgo / 60);
        return relativeTimeFormatter.format(-minutesAgo, 'minute');
    }

    return new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit'
    }).format(placedAt);
}

function PublicBidHistoryModal({isOpen, closeModal, items = [], isLoading}) {
    const context = useContext(WebsiteContext);
    const closeButtonRef = useRef(null);
    const totalBids = items.length;

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={closeModal} initialFocus={closeButtonRef}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30"/>
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel
                                className="relative mx-auto flex w-full max-w-2xl flex-col rounded-theme bg-white p-5 text-left shadow-lg"
                                data-popup=""
                            >
                                <div className="max-h-[70vh] overflow-y-auto" style={{overscrollBehavior: 'contain'}}>
                                    <section className="bg-white p-4">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <Dialog.Title as="h3" className="!mb-0 !text-lg font-semibold text-gray-900">
                                                Bid History
                                            </Dialog.Title>
                                            {items.length > 0 &&
                                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                    {items.length} bid{items.length === 1 ? '' : 's'}
                                                </span>
                                            }
                                        </div>

                                        {isLoading &&
                                            <div className="pt-4 text-sm text-gray-600">
                                                Loading bid history…
                                            </div>
                                        }

                                        {!isLoading && items.length === 0 &&
                                            <div className="pt-4 text-sm text-gray-600">
                                                No bids yet. When a bid comes in, it will show here.
                                            </div>
                                        }

                                        {!isLoading && items.length > 0 &&
                                            <div className="pt-4">
                                                {items.map((item, index) => (
                                                    <div
                                                        key={`${item.bidderLabel}-${item.placedAtUtc}-${item.amount}-${index}`}
                                                        className="flex items-start justify-between gap-4 border-t border-gray-200 py-3 first:border-t-0 first:pt-0 last:pb-0"
                                                    >
                                                        <div className="min-w-0">
                                                            <div className="text-base font-semibold text-gray-900">{item.bidderLabel}</div>
                                                            <div className="text-sm text-gray-600">{formatPlacedAt(item.placedAtUtc)}</div>
                                                        </div>
                                                        <div className="shrink-0 text-right text-base font-semibold text-gray-900">
                                                            <Currency.DisplayValue
                                                                currency={context.currency}
                                                                country={context.country}
                                                                value={item.amount}
                                                                emptyPlaceholder=""
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                    </section>
                                </div>

                                <div className="mt-4 text-center">
                                    <button
                                        ref={closeButtonRef}
                                        onClick={closeModal}
                                        type="button"
                                        className="text-base font-semibold text-link"
                                    >
                                        Close
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}

export default PublicBidHistoryModal;
